import {useCallback, useEffect, useMemo, useState} from 'react'
import {DropdownOptionProps} from 'wix-ui-tpa/cssVars'
import {useBi, useEnvironment, useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import {zonedTimeToUtc} from '@wix/table-reservations-lib/timezone'
import {
  ApprovalMode,
  ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import {
  getInitialReservationLocation,
  getReservationLocationById,
  getReservationLocationsOptions,
} from '../../../../../utils/reservationLocation'
import {
  getInitialPartySizeFromOptions,
  getPartySizeOptionsForReservationLocation,
} from '../../../../../utils/partySize'
import {filterDate} from '../../../../../utils/date'
import {getBusinessScheduleFromReservationLocation} from '../../../../../utils/businessSchedule'
import {TimeOption} from '../../../../../utils/timeOptions'
import {useReservationAddonStorage} from '../../../storage'
import {FieldName, getLogger} from '../../../../../utils/getLogger'
import {useElementWidth} from '../../../../../utils/useElementWidth'
import {useSelectedDateAndTime} from '../../../../../utils/useSelectedDateAndTime'
import {getReservationAddOnDataHookWithId} from '../../constants'
import {LayoutSize} from '../../../../../components-shared/LayoutSizeProvider/types'
import {EXPERIMENTS} from '../../../../../utils/constants'

export const useHooks = (
  enabledReservationLocations: ReservationLocation[],
  layoutSize: LayoutSize,
) => {
  const {experiments} = useExperiments()
  const shouldUseNewApprovalFields = experiments.enabled(EXPERIMENTS.useNewApprovalFields)
  const {goToReservationsPage, goToDetailsPage, regionalSettings, compId, isSiteBusinessPremium} =
    useReservationAddonStorage()

  // reservations unavailable
  const [shouldShowReservationsUnavailable, setShouldShowReservationsUnavailable] = useState(false)

  const handleHideReservationsUnavailable = useCallback(() => {
    setShouldShowReservationsUnavailable(false)
  }, [])

  const {isPreview, isSSR} = useEnvironment()

  const bi = useBi()
  const logger = getLogger(bi)

  const {t} = useTranslation()
  const {isMobile} = useEnvironment()

  const reservationAddOnDataHookWithId = useMemo(
    () => getReservationAddOnDataHookWithId(compId),
    [compId],
  )

  const {width: submitButtonContentWidth, readAndSetWidth: readAndSetSubmitButtonContentWidth} =
    useElementWidth<HTMLButtonElement>(reservationAddOnDataHookWithId)
  const [isLoading, setIsLoading] = useState(false)

  const [selectedReservationLocation, setSelectedReservationLocation] = useState<
    ReservationLocation | undefined
  >(getInitialReservationLocation(enabledReservationLocations))

  useEffect(() => {
    setSelectedReservationLocation(getInitialReservationLocation(enabledReservationLocations))
  }, [enabledReservationLocations])

  const partySizeOptions = useMemo(
    () =>
      getPartySizeOptionsForReservationLocation(
        t,
        selectedReservationLocation,
        shouldUseNewApprovalFields,
      ),
    [selectedReservationLocation],
  )

  const [selectedPartySize, setSelectedPartySize] = useState(
    getInitialPartySizeFromOptions(partySizeOptions),
  )

  useEffect(() => {
    setSelectedPartySize(getInitialPartySizeFromOptions(partySizeOptions))
  }, [partySizeOptions])

  const [isManualApproval, setIsManualApproval] = useState(false)

  useEffect(() => {
    if (shouldUseNewApprovalFields) {
      const manualApprovalConfig =
        selectedReservationLocation?.configuration?.onlineReservations?.approval

      const partySizeThreshold =
        manualApprovalConfig?.manualForLargePartiesOptions?.partySizeThreshold || 0

      const shouldBeManualApprove =
        manualApprovalConfig?.mode === ApprovalMode.MANUAL ||
        (manualApprovalConfig?.mode === ApprovalMode.MANUAL_FOR_LARGE_PARTIES &&
          selectedPartySize >= partySizeThreshold)

      setIsManualApproval(shouldBeManualApprove)
    } else {
      const manualApprovalConfig =
        selectedReservationLocation?.configuration?.onlineReservations?.manualApproval

      const shouldBeManualApprove =
        !!manualApprovalConfig?.enabled &&
        selectedPartySize >= manualApprovalConfig.partySizeThreshold!

      setIsManualApproval(shouldBeManualApprove)
    }
  }, [selectedPartySize, selectedReservationLocation, shouldUseNewApprovalFields])

  const reservationLocationsOptions: DropdownOptionProps[] = useMemo(
    () => getReservationLocationsOptions(enabledReservationLocations, t),
    [enabledReservationLocations],
  )

  const businessSchedule = useMemo(
    () => getBusinessScheduleFromReservationLocation(selectedReservationLocation),
    [selectedReservationLocation],
  )

  const {selectedDate, selectedTime, timeOptions, onSelectedDateChange, onSelectedTimeChange} =
    useSelectedDateAndTime({
      businessSchedule,
      regionalSettings,
      timeZone: selectedReservationLocation?.location?.timeZone,
    })

  const clickOnReservationsAttributesBiParams = useMemo(
    () => ({
      isAddOn: true,
      isMultiLocation: !!enabledReservationLocations.length,
      isPreview,
      locationId: selectedReservationLocation?.location?.id,
      reservationLocationId: selectedReservationLocation?.id,
    }),
    [enabledReservationLocations.length, isPreview, selectedReservationLocation?.id],
  )

  const handleFilterDate = useCallback(
    (date: Date) =>
      filterDate({
        date,
        businessSchedule,
        timeZone: selectedReservationLocation?.location?.timeZone,
        regionalFormat: regionalSettings,
      }),
    [businessSchedule, selectedReservationLocation?.location?.timeZone, regionalSettings],
  )

  const handleLocationChange = (option: DropdownOptionProps) => {
    const newSelectedReservationLocation =
      getReservationLocationById(option.id!, enabledReservationLocations) ??
      enabledReservationLocations[0]

    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      locationId: newSelectedReservationLocation?.location?.id,
      reservationLocationId: newSelectedReservationLocation?.id,
      fieldName: FieldName.location,
    })

    setSelectedReservationLocation(newSelectedReservationLocation)
  }

  const handlePartySizeChange = (option: DropdownOptionProps) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      fieldName: FieldName.partySize,
    })
    setSelectedPartySize(Number(option.id))
  }

  const handleDateChange = (date: string | Date) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      fieldName: FieldName.date,
    })

    onSelectedDateChange(date as Date)
  }

  const handleTimeChange = (option: TimeOption) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      fieldName: FieldName.time,
    })

    onSelectedTimeChange(option)
  }

  const handleFindTableSubmit = async (e) => {
    e.preventDefault()

    const isPartySizeExceedsMax = selectedPartySize >= partySizeOptions.length

    readAndSetSubmitButtonContentWidth()

    if (!selectedReservationLocation?.id || !selectedDate || !selectedTime) {
      return null
    }

    logger.clickOnFindATable({
      datetime: selectedDate,
      isMultiLocation: !!reservationLocationsOptions.length,
      locationId: selectedReservationLocation?.location?.id,
      reservationLocationId: selectedReservationLocation?.id,
      isPreview,
      partySize: selectedPartySize,
    })

    if (
      !selectedReservationLocation.configuration?.onlineReservations?.onlineReservationsEnabled ||
      !isSiteBusinessPremium
    ) {
      setShouldShowReservationsUnavailable(true)
      return
    }

    setIsLoading(true)

    const params = {
      reservationData: {
        startDate: zonedTimeToUtc(selectedDate, selectedReservationLocation?.location?.timeZone),
        partySize: selectedPartySize,
        reservationLocationId: selectedReservationLocation.id!,
      },
    }

    if (isManualApproval && !isPartySizeExceedsMax) {
      await goToDetailsPage(params)
    } else {
      await goToReservationsPage(params)
    }
  }

  return {
    t,
    isSSR,
    submitButtonContentWidth,
    isMobile,
    regionalSettings,
    reservationLocationsOptions,
    isLoading,
    partySizeOptions,
    timeOptions,
    selectedReservationLocation,
    selectedPartySize,
    selectedDate,
    selectedTime,
    handleLocationChange,
    handlePartySizeChange,
    handleDateChange,
    handleTimeChange,
    handleFindTableSubmit,
    filterDate: handleFilterDate,
    shouldShowReservationsUnavailable,
    handleHideReservationsUnavailable,
    reservationAddOnDataHookWithId,
  }
}
